import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  ConfirmationDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  RadioButtonGroup,
  Textarea,
} from '@roger/ui-components';
import { FileText, Loader2, Save, Trash2, Upload } from 'lucide-react';
import {
  deleteTaskDetailsDocument,
  getTaskDetails,
  getTaskDocumentUrl,
  updateTaskDetailsNotes,
  updateTaskDetailsVisitFields,
} from '../../services/api';
import type {
  TaskDetailsResponse,
  VisitTaskDetailFields,
  VisitTaskDetailFieldsUpdate,
} from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

export interface TaskDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string;
  /** Owning patient, so an upload also refreshes the patient's document count. */
  patientId?: string;
  /** Names the task in the upload progress toast. */
  targetLabel?: string;
  /** Holds the edit permission. Without it everything here is read-only. */
  canEdit: boolean;
  /** The saved note, so the surface behind the dialog can show it too. */
  onNotesSaved?: (notes: string) => void;
  /** An attachment was added or removed. */
  onDocumentsChanged?: () => void;
  /**
   * A visit's saved values, so an open form behind the dialog can show them,
   * and the names of the fields this save wrote.
   */
  onVisitFieldsSaved?: (
    fields: VisitTaskDetailFields,
    written: readonly string[]
  ) => void;
}

/** The form's own answers for where care was provided, by code. */
const SITE_OF_SERVICE_OPTIONS = [
  { value: 'Q5001', label: '(Q5001) Patient\u2019s home or residence' },
  { value: 'Q5002', label: '(Q5002) Assisted living facility' },
  { value: 'Q5009', label: '(Q5009) Place not otherwise specified' },
];

const HCPCS_CODE = /^[A-Za-z][0-9]{4}$/;
const MILEAGE = /^[0-9]{1,6}(\.[0-9]{1,2})?$/;

interface VisitFieldsDraft {
  site: string;
  override: string;
  mileage: string;
}

const EMPTY_VISIT_FIELDS: VisitFieldsDraft = {
  site: '',
  override: '',
  mileage: '',
};

/** Miles as typed text: the server sends two decimals, the user types few. */
function mileageText(mileage: string | null | undefined): string {
  if (mileage === null || mileage === undefined || mileage === '') return '';
  const miles = Number(mileage);
  return Number.isFinite(miles) ? String(miles) : '';
}

function toDraft(fields: VisitTaskDetailFields): VisitFieldsDraft {
  return {
    site: fields.site_of_service_code ?? '',
    override: fields.hcpcs_code_override ?? '',
    mileage: mileageText(fields.mileage),
  };
}

/** The body for a save: only what differs from what the server last sent. */
function visitFieldsChanges(
  draft: VisitFieldsDraft,
  saved: VisitFieldsDraft
): VisitTaskDetailFieldsUpdate {
  const changes: VisitTaskDetailFieldsUpdate = {};
  if (draft.site !== saved.site) {
    changes.site_of_service_code = draft.site || null;
  }
  const override = draft.override.trim().toUpperCase();
  if (override !== saved.override) {
    changes.hcpcs_code_override = override || null;
  }
  const mileage = draft.mileage.trim();
  if (mileageText(mileage) !== saved.mileage) {
    changes.mileage = mileage === '' ? null : mileage;
  }
  return changes;
}

/**
 * A task's details: what is kept beside its note rather than inside it, and so
 * stays editable after the note is signed: the sticky note, the attachments,
 * and for a visit its location of care, HCPCS code and mileage.
 *
 * Reads and writes go through the Task Details routes, which check the view
 * and edit permissions on the server. `canEdit` only decides which controls to
 * draw.
 */
export function TaskDetailsDialog({
  open,
  onOpenChange,
  taskId,
  patientId,
  targetLabel,
  canEdit,
  onNotesSaved,
  onDocumentsChanged,
  onVisitFieldsSaved,
}: TaskDetailsDialogProps) {
  const { error: showError, success: showSuccess } = useToast();
  const startTaskDocumentUpload = useStore(
    state => state.startTaskDocumentUpload
  );
  const uploadTick = useStore(state => state.taskUploadTick[taskId] ?? 0);

  const [details, setDetails] = useState<TaskDetailsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [draftNotes, setDraftNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [draftFields, setDraftFields] =
    useState<VisitFieldsDraft>(EMPTY_VISIT_FIELDS);
  const [savingFields, setSavingFields] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Newest request wins, so a slow response for one task cannot land on
  // another after the panel behind the dialog has moved on.
  const requestSeqRef = useRef(0);
  // Whether the draft has been filled from the server since the dialog opened.
  // The first load to land fills it, whichever request that is; later loads
  // leave it alone, so a refresh never discards what the user is typing.
  const draftSeededRef = useRef(false);

  const load = useCallback(async () => {
    const seq = (requestSeqRef.current += 1);
    setLoading(true);
    try {
      const { data } = await getTaskDetails({
        path: { task_id: taskId },
        throwOnError: true,
      });
      if (requestSeqRef.current !== seq) return;
      setDetails(data);
      setLoadFailed(false);
      if (!draftSeededRef.current) {
        draftSeededRef.current = true;
        setDraftNotes(data.notes);
        setDraftFields(
          data.visit_fields ? toDraft(data.visit_fields) : EMPTY_VISIT_FIELDS
        );
      }
    } catch (err) {
      if (requestSeqRef.current !== seq) return;
      console.error('Failed to load task details', err);
      setLoadFailed(true);
    } finally {
      if (requestSeqRef.current === seq) setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    if (!open) return;
    setDetails(null);
    setLoadFailed(false);
    setDraftNotes('');
    setDraftFields(EMPTY_VISIT_FIELDS);
    draftSeededRef.current = false;
    void load();
  }, [open, load]);

  // A background upload for this task finished: show it, without throwing away
  // a note the user is partway through typing.
  const seenUploadTickRef = useRef(uploadTick);
  useEffect(() => {
    if (uploadTick === seenUploadTickRef.current) return;
    seenUploadTickRef.current = uploadTick;
    if (!open) return;
    void load();
    onDocumentsChanged?.();
  }, [uploadTick, open, load, onDocumentsChanged]);

  // On a misc task and a communication the notes are the task's own content,
  // and on a misc task so are the documents. Once such a task is signed the
  // server refuses changes to them, whoever asks.
  const notesLocked = details?.notes_locked ?? false;
  const documentsLocked = details?.documents_locked ?? false;
  const notesEditable = canEdit && !notesLocked && details !== null;
  const documentsEditable = canEdit && !documentsLocked && details !== null;
  const notesAreContent =
    details?.task_type === 'misc_task' ||
    details?.task_type === 'patient_communication';
  const notesLabel = notesAreContent ? 'Notes' : 'Sticky note';
  const description = notesLocked
    ? documentsLocked
      ? 'This task is signed, so its notes and attachments are read-only.'
      : 'This task is signed, so its notes are read-only.'
    : canEdit
      ? 'These stay editable after the note is signed.'
      : 'You can view these but not change them.';
  const notesChanged = details !== null && draftNotes !== details.notes;

  // Only a visit has these. They are never signed content, so the edit
  // permission alone decides whether they can be changed.
  const visitFields = details?.visit_fields ?? null;
  const fieldsEditable = canEdit && visitFields !== null;
  const savedFields = visitFields ? toDraft(visitFields) : EMPTY_VISIT_FIELDS;
  const fieldChanges = visitFieldsChanges(draftFields, savedFields);
  const fieldsChanged = Object.keys(fieldChanges).length > 0;
  const overrideInvalid =
    draftFields.override.trim() !== '' &&
    !HCPCS_CODE.test(draftFields.override.trim());
  const mileageInvalid =
    draftFields.mileage.trim() !== '' &&
    !MILEAGE.test(draftFields.mileage.trim());
  const siteOptions =
    draftFields.site === '' ||
    SITE_OF_SERVICE_OPTIONS.some(option => option.value === draftFields.site)
      ? SITE_OF_SERVICE_OPTIONS
      : [
          ...SITE_OF_SERVICE_OPTIONS,
          { value: draftFields.site, label: draftFields.site },
        ];

  const handleSaveFields = async () => {
    const submitted = draftFields;
    const changes = visitFieldsChanges(submitted, savedFields);
    setSavingFields(true);
    try {
      const { data } = await updateTaskDetailsVisitFields({
        path: { task_id: taskId },
        body: changes,
        throwOnError: true,
      });
      setDetails(prev => (prev ? { ...prev, visit_fields: data } : prev));
      // Anything typed while the save was in flight stays, still unsaved.
      setDraftFields(current =>
        current === submitted ? toDraft(data) : current
      );
      onVisitFieldsSaved?.(data, Object.keys(changes));
      showSuccess('Details saved');
    } catch (err) {
      console.error('Failed to save visit details', err);
      showError(getBusinessErrorMessage(err) ?? 'Failed to save details');
      void load();
    } finally {
      setSavingFields(false);
    }
  };

  const handleSaveNotes = async () => {
    const submitted = draftNotes;
    setSaving(true);
    try {
      const { data } = await updateTaskDetailsNotes({
        path: { task_id: taskId },
        body: { notes: submitted },
        throwOnError: true,
      });
      setDetails(prev => (prev ? { ...prev, notes: data.notes } : prev));
      // Anything typed while the save was in flight stays, still unsaved.
      setDraftNotes(current => (current === submitted ? data.notes : current));
      onNotesSaved?.(data.notes);
      showSuccess('Note saved');
    } catch (err) {
      console.error('Failed to save note', err);
      showError(getBusinessErrorMessage(err) ?? 'Failed to save note');
      // The task may have been signed since the dialog opened, which locks it.
      void load();
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset immediately so re-selecting the same file fires onChange again.
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    // Fire-and-forget: the store owns the progress toast and bumps this task's
    // upload tick on success, which the effect above turns into a refresh.
    void startTaskDocumentUpload({
      taskId,
      patientId,
      file,
      targetLabel,
      viaTaskDetails: true,
    });
  };

  const handleOpenDocument = async (documentId: string) => {
    try {
      const { data } = await getTaskDocumentUrl({
        path: { task_id: taskId, document_id: documentId },
        throwOnError: true,
      });
      if (!data?.url) throw new Error('Document URL missing from response');
      window.open(data.url, '_blank');
    } catch (err) {
      console.error('Failed to get document URL', err);
      showError('Failed to open attachment. Please try again.');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDeleteId) return;
    setDeleting(true);
    try {
      await deleteTaskDetailsDocument({
        path: { task_id: taskId, document_id: pendingDeleteId },
        throwOnError: true,
      });
      setDetails(prev =>
        prev
          ? {
              ...prev,
              documents: prev.documents.filter(
                doc => doc.id !== pendingDeleteId
              ),
            }
          : prev
      );
      onDocumentsChanged?.();
    } catch (err) {
      console.error('Failed to delete attachment', err);
      showError(getBusinessErrorMessage(err) ?? 'Failed to delete attachment');
      void load();
    } finally {
      setDeleting(false);
      setPendingDeleteId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Task Details</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {details === null ? (
          loadFailed ? (
            <div className='flex items-center justify-between gap-2 py-4'>
              <p className='text-sm text-destructive'>
                Couldn&apos;t load task details.
              </p>
              <Button size='sm' variant='outline' onClick={() => void load()}>
                Retry
              </Button>
            </div>
          ) : (
            <div
              className='flex items-center gap-2 py-4 text-sm text-muted-foreground'
              role='status'
            >
              <Loader2 className='h-4 w-4 animate-spin' aria-hidden='true' />
              Loading...
            </div>
          )
        ) : (
          // min-w-0: the dialog lays out as a grid, and without it a long
          // attachment name widens the column past the dialog's right edge.
          <div className='min-w-0 space-y-5'>
            <div className='space-y-2'>
              <Label htmlFor='task-details-notes'>{notesLabel}</Label>
              <Textarea
                id='task-details-notes'
                value={draftNotes}
                onChange={e => setDraftNotes(e.target.value)}
                placeholder={notesEditable ? 'Add a note...' : 'No note.'}
                className='min-h-[120px]'
                readOnly={!notesEditable}
              />
              {notesEditable && (
                <div className='flex justify-end'>
                  <Button
                    size='sm'
                    onClick={() => void handleSaveNotes()}
                    disabled={saving || !notesChanged}
                  >
                    {saving ? (
                      <Loader2
                        className='h-4 w-4 animate-spin'
                        aria-hidden='true'
                      />
                    ) : (
                      <Save className='h-4 w-4' aria-hidden='true' />
                    )}
                    {saving ? 'Saving...' : 'Save note'}
                  </Button>
                </div>
              )}
            </div>

            {visitFields !== null && (
              <div className='space-y-3'>
                <h3 className='text-sm font-medium leading-none'>
                  Billing and payroll
                </h3>
                <div className='space-y-2'>
                  <Label id='task-details-site-label'>Location of care</Label>
                  <RadioButtonGroup
                    aria-labelledby='task-details-site-label'
                    options={siteOptions}
                    value={draftFields.site}
                    onValueChange={site =>
                      setDraftFields(prev => ({ ...prev, site }))
                    }
                    disabled={!fieldsEditable}
                    allowDeselect
                    orientation='vertical'
                    className='gap-2'
                  />
                </div>
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                  <div className='space-y-2'>
                    <Label htmlFor='task-details-hcpcs'>HCPCS code</Label>
                    <Input
                      id='task-details-hcpcs'
                      value={draftFields.override}
                      onChange={e =>
                        setDraftFields(prev => ({
                          ...prev,
                          override: e.target.value,
                        }))
                      }
                      placeholder={
                        visitFields.hcpcs_code_default
                          ? `Default ${visitFields.hcpcs_code_default}`
                          : 'Default'
                      }
                      maxLength={5}
                      readOnly={!fieldsEditable}
                      aria-invalid={overrideInvalid}
                      aria-describedby='task-details-hcpcs-help'
                    />
                    <p
                      id='task-details-hcpcs-help'
                      className={
                        overrideInvalid
                          ? 'text-xs text-destructive'
                          : 'text-xs text-muted-foreground'
                      }
                    >
                      {overrideInvalid
                        ? 'A letter and four digits, such as G0299.'
                        : 'Leave blank to use the default.'}
                    </p>
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='task-details-mileage'>Mileage</Label>
                    <Input
                      id='task-details-mileage'
                      inputMode='decimal'
                      value={draftFields.mileage}
                      onChange={e =>
                        setDraftFields(prev => ({
                          ...prev,
                          mileage: e.target.value,
                        }))
                      }
                      placeholder={fieldsEditable ? 'Miles' : 'Not recorded'}
                      readOnly={!fieldsEditable}
                      aria-invalid={mileageInvalid}
                      aria-describedby={
                        mileageInvalid ? 'task-details-mileage-help' : undefined
                      }
                    />
                    {mileageInvalid && (
                      <p
                        id='task-details-mileage-help'
                        className='text-xs text-destructive'
                      >
                        Miles as a number, such as 12.5.
                      </p>
                    )}
                  </div>
                </div>
                {fieldsEditable && (
                  <div className='flex justify-end'>
                    <Button
                      size='sm'
                      onClick={() => void handleSaveFields()}
                      disabled={
                        savingFields ||
                        !fieldsChanged ||
                        overrideInvalid ||
                        mileageInvalid
                      }
                    >
                      {savingFields ? (
                        <Loader2
                          className='h-4 w-4 animate-spin'
                          aria-hidden='true'
                        />
                      ) : (
                        <Save className='h-4 w-4' aria-hidden='true' />
                      )}
                      {savingFields ? 'Saving...' : 'Save details'}
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div className='space-y-2'>
              <div className='flex items-center justify-between gap-2'>
                <h3 className='text-sm font-medium leading-none'>
                  Attachments
                </h3>
                {loading && (
                  <Loader2
                    className='h-4 w-4 animate-spin text-muted-foreground'
                    aria-label='Refreshing'
                  />
                )}
              </div>
              {details.documents.length === 0 ? (
                <p className='text-sm text-muted-foreground'>No attachments.</p>
              ) : (
                <ul className='max-h-48 divide-y overflow-y-auto rounded-md border'>
                  {details.documents.map(doc => (
                    <li
                      key={doc.id}
                      className='flex items-center gap-2 px-3 py-2'
                    >
                      <button
                        type='button'
                        className='flex min-w-0 flex-1 items-center gap-2 text-left text-sm hover:underline'
                        onClick={() => void handleOpenDocument(doc.id)}
                      >
                        <FileText
                          className='h-4 w-4 shrink-0 text-blue-500'
                          aria-hidden='true'
                        />
                        <span className='truncate'>{doc.file_name}</span>
                      </button>
                      {documentsEditable && (
                        <Button
                          size='sm'
                          variant='ghost'
                          onClick={() => setPendingDeleteId(doc.id)}
                          aria-label={`Delete ${doc.file_name}`}
                        >
                          <Trash2
                            className='h-4 w-4 text-destructive'
                            aria-hidden='true'
                          />
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {documentsEditable && (
                <>
                  <input
                    ref={fileInputRef}
                    type='file'
                    className='hidden'
                    onChange={handleUpload}
                    accept='.pdf,.png,.jpg,.jpeg,.doc,.docx'
                    aria-label='Upload attachment'
                  />
                  <Button
                    size='sm'
                    variant='outline'
                    className='w-full sm:w-auto'
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className='h-4 w-4' aria-hidden='true' />
                    Upload attachment
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
      <ConfirmationDialog
        open={pendingDeleteId !== null}
        title='Delete attachment'
        message='Are you sure you want to delete this attachment? This action cannot be undone.'
        confirmLabel='Delete'
        cancelLabel='Cancel'
        loading={deleting}
        onAccept={() => void handleDeleteConfirm()}
        onCancel={() => setPendingDeleteId(null)}
      />
    </Dialog>
  );
}
